import { Box } from '@mui/material';
import { Plus, Microphone } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import { contrastOn } from '@/utils/colorMath';
import { useAiUsable } from '@/utils/ai';

type CreateActivityFabProps = {
  /** Callback för att öppna röstinspelning. Om null/undefined visas ingen mic-FAB. */
  onVoiceClick?: () => void;
};

/**
 * Gemensam FAB för att skapa aktivitet (+ optional röst-FAB). Ligger ALLTID i
 * SAMMA fixed position oavsett persona: nere till höger, ovanför navbaren.
 *
 * Färger hämtas från det aktiva temat (ThemePreferenceContext).
 */
const CreateActivityFab = ({ onVoiceClick }: CreateActivityFabProps) => {
  const aiUsable = useAiUsable();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const clearForm = () => sessionStorage.removeItem('formActivity');

  const handleCreate = () => {
    clearForm();
    navigate('/create-activity/Title');
  };

  const fabSx = {
    position: 'fixed' as const,
    bottom: { xs: 90, sm: 100 },
    width: 56,
    height: 56,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    zIndex: 50,
    fontFamily: 'inherit',
    transition: 'transform 0.15s',
    '@media (hover: hover)': { '&:hover': { transform: 'scale(1.1)' } },
    '&:active': { transform: 'scale(0.95)' },
  };

  return (
    <>
      {/* Sekundär FAB: röstinspelning. Tolkningen av det inspelade görs av AI
          (/ai/voice/parse), så utan nyckel i miljön leder knappen till ett 500 efter att
          man redan talat in. Då ska den inte finnas. Gaten sitter HÄR och inte hos de fem
          anroparna — ett ställe, samma svar överallt. */}
      {onVoiceClick && aiUsable && (
        <Box
          component="button"
          type="button"
          onClick={onVoiceClick}
          aria-label={t('voiceActivity', 'Skapa aktivitet med röst') as string}
          sx={{
            ...fabSx,
            right: { xs: 84, sm: 92 },
            bgcolor: tk.accent,
            color: contrastOn(tk.accent),
            fontSize: '1.5rem',
            boxShadow: `0 4px 16px ${tk.accent}44`,
          }}
        >
          <Microphone size={24} weight="bold" />
        </Box>
      )}

      {/* Primär FAB: skapa ny aktivitet */}
      <Box
        component="button"
        type="button"
        onClick={handleCreate}
        aria-label={t('Add new activity') as string}
        sx={{
          ...fabSx,
          right: { xs: 20, sm: 24 },
          bgcolor: tk.primary,
          // Vitt i ljust läge, nästan svart i mörkt — det som ger högst kontrast.
          color: contrastOn(tk.primary),
          fontSize: '1.6rem',
          boxShadow: `0 4px 16px ${tk.primary}66`,
        }}
      >
        <Plus size={26} weight="bold" />
      </Box>
    </>
  );
};

export default CreateActivityFab;
